import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { roundHelper } from 'lib/helpers/round.helper.class';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';

export interface IConvertedProgress {
	readonly unit: ProgressTypeDb.ProgressTypeUnit;
	readonly value: number;
}

export type MeasurementsOverview = {
	readonly usedUnit: ProgressTypeDb.ProgressTypeUnit;
	readonly measurements: number;
	readonly minValue: number;
	readonly maxValue: number;
	readonly avgValue: number;
};

export class ProgressHelper {
	convertValueToPrefferedFormat(data: {
		unit: ProgressTypeDb.ProgressTypeUnit | undefined;
		value: number;
		preferredUnit: UserDb.MeasurementUnitType | undefined;
		roundValue?: boolean;
	}): IConvertedProgress {
		if (data.unit === 'cm' && data.preferredUnit === 'imperial') {
			// convert cm to imperial units
			return {
				value: unitConversionHelper.convertCmToInches(data.value, data.roundValue ?? false),
				unit: 'inches'
			};
		}

		if (data.unit === 'inches' && data.preferredUnit === 'metric') {
			// convert inches to metric units
			return {
				value: unitConversionHelper.convertInchesToCm(data.value, data.roundValue ?? false),
				unit: 'cm'
			};
		}

		if (data.unit === 'kg' && data.preferredUnit === 'imperial') {
			// convert kg to imperial units
			return {
				value: unitConversionHelper.convertKgToLbs(data.value, data.roundValue ?? false),
				unit: 'pounds'
			};
		}

		if (data.unit === 'pounds' && data.preferredUnit === 'metric') {
			// convert pounds to metric units
			return {
				value: unitConversionHelper.convertLbsToKg(data.value, data.roundValue ?? false),
				unit: 'kg'
			};
		}

		if (data.unit === 'meters' && data.preferredUnit === 'imperial') {
			// convert to imperial units
			return {
				value: unitConversionHelper.convertMetersToFeets(data.value, data.roundValue ?? false),
				unit: 'feets'
			};
		}

		if (data.unit === 'feets' && data.preferredUnit === 'metric') {
			// convert to metric units
			return {
				value: unitConversionHelper.convertFeetsToMeters(data.value, data.roundValue ?? false),
				unit: 'meters'
			};
		}

		// no changes needed
		return {
			unit: data.unit ?? 'value',
			value: unitConversionHelper.roundUnitValue(data.value, data.roundValue)
		};
	}

	calculateMeasurementsOverview(
		values: readonly UserDb.IUserAssignedProgressTypeValue[],
		authUser: UserDb.IAuthUser | undefined,
		unit: ProgressTypeDb.ProgressTypeUnit | undefined
	): MeasurementsOverview {
		let minValue: number | undefined;
		let maxValue: number | undefined;
		let sumValues: number = 0;

		const usedUnit: ProgressTypeDb.ProgressTypeUnit = progressHelper.convertValueToPrefferedFormat({
			preferredUnit: authUser?.preferredUnit,
			unit: unit,
			value: 0
		}).unit;

		for (const progressTypeValue of values) {
			const convertedValue: number = progressHelper.convertValueToPrefferedFormat({
				preferredUnit: authUser?.preferredUnit,
				unit: unit,
				value: progressTypeValue.value
			}).value;

			sumValues += convertedValue;

			if (minValue) {
				if (convertedValue < minValue) {
					minValue = convertedValue;
				}
			} else {
				minValue = convertedValue;
			}

			if (maxValue) {
				if (convertedValue > maxValue) {
					maxValue = convertedValue;
				}
			} else {
				maxValue = convertedValue;
			}
		}

		return {
			usedUnit: usedUnit,
			measurements: values.length,
			minValue: roundHelper.roundTo(minValue ?? 0, 1),
			maxValue: roundHelper.roundTo(maxValue ?? 0, 1),
			avgValue: roundHelper.roundTo(sumValues / values.length, 1)
		};
	}
}

export const progressHelper = new ProgressHelper();
